import { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Stack,
  Grid,
  LinearProgress,
} from '@mui/material';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import questionsData from '../../mocks/questions.json';
import patient360Data from '../../mocks/patient360Data.json';
import { useAuth } from '../../auth/useAuth';
import {
  PatientQuestionHistorySection,
  type PatientQuestionHistoryItem,
} from '../../components/patient';

interface Question extends PatientQuestionHistoryItem {
  id: string;
  patientId: string;
  nutritionistId: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
}

export const QuestionsPage = () => {
  const { user } = useAuth();
  const [selectedNutritionist, setSelectedNutritionist] = useState(
    questionsData.availableNutritionists[0].id
  );
  const [questionText, setQuestionText] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [questions, setQuestions] = useState<Question[]>(questionsData.questions as Question[]);
  const [allowance, setAllowance] = useState(questionsData.questionAllowance);

  const resolvedPatientId = user?.id;

  const generateAIDraft = (question: string, questionId: string, patientId: string, nutritionistId: string) => {
    const patient = patient360Data.assignedPatients[0];

    const aiResponse = {
      id: `draft-${Date.now()}`,
      questionId,
      patientId,
      nutritionistId,
      content: generateResponseContent(question),
      reasoning: `AI analyzed patient's question in context of their protocol, recent CGM data, and meal patterns. Patient ${patient.firstName} ${patient.lastName} has ${patient.dataCompleteness}% data completeness and is classified as ${patient.riskLevel} risk with ${patient.cgmStatus} CGM status.`,
      evidenceRefs: [
        {
          refType: 'PATIENT_QUESTION',
          refId: questionId,
          label: `Patient Question: ${question.substring(0, 50)}...`
        },
        {
          refType: 'CGM_EVENT',
          refId: 'cgm-latest',
          label: 'Recent CGM data (14 days)'
        },
        {
          refType: 'PROTOCOL_VERSION',
          refId: 'protocol-001',
          label: 'Active Stoplight Protocol'
        }
      ],
      status: 'PENDING_REVIEW',
      aiModel: 'claude-3-sonnet-20240229',
      generatedAt: new Date().toISOString(),
      patient: {
        firstName: patient.firstName,
        lastName: patient.lastName
      }
    };

    const existingDrafts = JSON.parse(localStorage.getItem('aiDrafts') || '[]');
    localStorage.setItem('aiDrafts', JSON.stringify([aiResponse, ...existingDrafts]));

    return aiResponse;
  };

  const generateResponseContent = (question: string): string => {
    const lowerQuestion = question.toLowerCase();

    if (lowerQuestion.includes('banana') || lowerQuestion.includes('bananas')) {
      return "Great question! Based on your CGM data and current protocol:\n\nBananas are classified as YELLOW foods in your personalized plan. This means you can enjoy them in moderation (1 small-medium banana). I recommend:\n\n1. Choose slightly less ripe bananas (they have lower glycemic impact)\n2. Pair with protein like Greek yogurt or nut butter\n3. Best consumed earlier in the day (breakfast or mid-morning snack)\n4. Your CGM data shows better glucose control with fruit in the morning vs. evening\n\nWould you like specific portion guidance or meal pairing suggestions?";
    }

    if (lowerQuestion.includes('rice') || lowerQuestion.includes('pasta')) {
      return "Good timing on this question! I've been reviewing your recent meal patterns:\n\nBased on your protocol:\n- White rice = YELLOW (1/2 cup cooked, pair with protein)\n- Brown rice = GREEN (1/2 cup cooked)\n- Pasta (white) = YELLOW (1 cup cooked)\n\nYour CGM data shows you handle these foods best when:\n1. Portioned carefully\n2. Paired with vegetables and protein\n3. Eaten earlier in the day\n\nWant to schedule a quick call to discuss meal planning strategies?";
    }

    if (lowerQuestion.includes('snack') || lowerQuestion.includes('evening') || lowerQuestion.includes('night')) {
      return "I'm glad you're asking about this! Your evening patterns are important:\n\nBest evening GREEN snacks:\n- Raw vegetables with hummus\n- Greek yogurt with berries\n- Small handful of almonds\n- Hard-boiled egg\n\nYour CGM data shows carb-heavy snacks after 8pm tend to cause spikes. I recommend:\n1. Focus on protein + healthy fat combinations\n2. Keep portions modest\n3. Try to finish eating 2-3 hours before bedtime\n\nYour recent data completeness is excellent (92%)! Keep it up!";
    }

    return `Thank you for your question! I've reviewed your recent data:\n\n✓ CGM Status: Active with good data coverage\n✓ Data Completeness: 92%\n✓ Risk Level: Low - great progress!\n\nBased on your personalized protocol and recent patterns, I recommend we discuss this during our next check-in. Your current approach with meal logging and CGM monitoring is working well.\n\nIn the meantime, continue following your GREEN/YELLOW/RED food classifications and let me know if you have any urgent concerns.\n\nWould you like to schedule a follow-up call this week?`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!questionText.trim() || allowance.remaining <= 0) return;

    const selectedNutritionistData = questionsData.availableNutritionists.find(
      (n) => n.id === selectedNutritionist
    );

    const newQuestion: Question = {
      id: `q-${Date.now()}`,
      patientId: resolvedPatientId ?? 'patient-001',
      nutritionistId: selectedNutritionist,
      nutritionistName: selectedNutritionistData?.name || '',
      question: questionText,
      status: 'PENDING',
      priority: 'MEDIUM',
      askedAt: new Date().toISOString()
    };

    setQuestions([newQuestion, ...questions]);
    setAllowance({
      ...allowance,
      used: allowance.used + 1,
      remaining: allowance.remaining - 1
    });

    generateAIDraft(questionText, newQuestion.id, newQuestion.patientId, selectedNutritionist);

    setQuestionText('');
    setShowSuccess(true);

    setTimeout(() => setShowSuccess(false), 5000);
  };

  const canAskQuestion = allowance.remaining > 0;
  const usagePercent = (allowance.used / allowance.totalAllowed) * 100;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Questions To Nutritionist
      </Typography>
      <Typography color="text.secondary" paragraph>
        Send a new question to your nutrition team.
      </Typography>

      {showSuccess && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          icon={<AutoAwesomeIcon />}
        >
          <strong>Question submitted successfully!</strong>
          <br />
          Our AI assistant is analyzing your question with your patient data and generating a draft response for your nutritionist to review. Your nutritionist will review, personalize, and send you a response within 24-48 hours.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QuestionAnswerIcon color="primary" />
              Your Question Plan
            </Typography>

            <Box sx={{ my: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Questions Used
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {allowance.used} / {allowance.totalAllowed}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={usagePercent}
                sx={{ height: 8, borderRadius: 4 }}
                color={usagePercent >= 100 ? 'error' : 'primary'}
              />
            </Box>

            <Alert
              severity={allowance.remaining > 0 ? 'info' : 'warning'}
              sx={{ mt: 2 }}
            >
              <Typography variant="body2">
                <strong>{allowance.remaining}</strong> question{allowance.remaining !== 1 ? '' : 's'} remaining
              </Typography>
              <Typography variant="caption" display="block">
                Resets in {allowance.resetsIn}
              </Typography>
            </Alert>

            <Divider sx={{ my: 2 }} />

            <Typography variant="caption" color="text.secondary">
              <strong>Plan Type:</strong> {allowance.planType === 'WEEKLY' ? 'Weekly' : 'Daily'}<br />
              <strong>Period:</strong> {new Date(allowance.periodStart).toLocaleDateString()} - {new Date(allowance.periodEnd).toLocaleDateString()}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Ask a New Question
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Send to Nutritionist</InputLabel>
                  <Select
                    value={selectedNutritionist}
                    label="Send to Nutritionist"
                    onChange={(e) => setSelectedNutritionist(e.target.value)}
                  >
                    {questionsData.availableNutritionists.map((nutritionist) => (
                      <MenuItem key={nutritionist.id} value={nutritionist.id}>
                        <Box>
                          <Typography variant="body2">
                            {nutritionist.name} {nutritionist.isPrimary && '(Primary)'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {nutritionist.credentials} • {nutritionist.responseTime}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Your Question"
                  multiline
                  rows={4}
                  value={questionText}
                  onChange={(e) => setQuestionText(e.target.value)}
                  placeholder="e.g., Can I eat bananas? Are they on my green or yellow list?"
                  fullWidth
                  required
                  disabled={!canAskQuestion}
                  helperText={
                    !canAskQuestion
                      ? `You've used all ${allowance.totalAllowed} questions for this ${allowance.planType.toLowerCase()}. Resets in ${allowance.resetsIn}.`
                      : `${questionText.length}/500 characters`
                  }
                  inputProps={{ maxLength: 500 }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  endIcon={<SendIcon />}
                  disabled={!canAskQuestion || !questionText.trim()}
                  fullWidth
                >
                  Submit Question
                </Button>
              </Stack>
            </Box>

            {!canAskQuestion && (
              <Alert severity="info" sx={{ mt: 2 }}>
                For urgent concerns, please contact your nutritionist directly or call the clinic.
              </Alert>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Your Question History
          </Typography>

          <PatientQuestionHistorySection questions={questions} />
        </Grid>
      </Grid>
    </Box>
  );
};
